import { useRef, useState } from 'react';
import { Camera, FileUp, Loader2, Upload } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import { FUENTE_LABELS, type Candidato, type Fuente } from '@/lib/candidatos';

/**
 * Alta a partir del currículum, que es como llega la mayoría.
 *
 * El caso real es este: alguien deja el CV en la oficina o lo manda por
 * WhatsApp, y quien lo recibe está de pie con el móvil en la mano. Se hace la
 * foto, se sube y la ficha existe. Los datos los saca después la IA.
 *
 * El alta y la lectura van en dos pasos a propósito: crear la ficha con el
 * fichero tarda un segundo, y leerlo con el razonador entre veinte y sesenta.
 * Si fuera todo junto, un fallo del modelo se llevaría por delante el CV.
 */
interface Props {
  onCreado: (c: Candidato) => void;
  onCerrar: () => void;
}

export default function NuevoDesdeCv({ onCreado, onCerrar }: Props) {
  const [fichero, setFichero] = useState<File | null>(null);
  const [telefono, setTelefono] = useState('');
  const [fuente, setFuente] = useState<Fuente>('whatsapp');
  const [subiendo, setSubiendo] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const camaraRef = useRef<HTMLInputElement>(null);

  const crear = async () => {
    if (!fichero) return;
    setSubiendo(true);
    setError(null);
    try {
      onCreado(await api.crearCandidatoDesdeCv(fichero, { telefono, fuente }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido subir el currículum');
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <Modal isOpen onClose={() => !subiendo && onCerrar()} title="Nuevo candidato desde su CV">
      <div className="space-y-4">
        <label
          className={classNames(
            'flex flex-col items-center justify-center gap-2 w-full rounded-xl border-2 border-dashed px-4 py-8 cursor-pointer transition-colors',
            arrastrando
              ? 'border-sereno-blue bg-sereno-blue/10'
              : 'border-sereno-dark/15 hover:border-sereno-blue/50 hover:bg-sereno-blue/5',
            subiendo && 'pointer-events-none opacity-60',
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastrando(false);
            const f = e.dataTransfer.files?.[0];
            if (f) setFichero(f);
          }}
        >
          {fichero ? (
            <>
              <FileUp size={24} className="text-sereno-dark/50" />
              <span className="text-sm font-medium text-center break-all px-2">{fichero.name}</span>
              <span className="text-xs text-sereno-dark/40">Toca para elegir otro</span>
            </>
          ) : (
            <>
              <Upload size={24} className="text-sereno-dark/30" />
              <span className="text-sm text-sereno-dark/50">
                <span className="font-medium">Arrastra el CV</span> o haz clic
              </span>
              <span className="text-xs text-sereno-dark/30">PDF, Word o una foto</span>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
            onChange={(e) => setFichero(e.target.files?.[0] || null)}
          />
        </label>

        {/* En móvil, la vía rápida: fotografiar el papel que se acaba de recibir */}
        <button
          type="button"
          onClick={() => camaraRef.current?.click()}
          disabled={subiendo}
          className="btn-ghost w-full h-11 text-sm flex items-center justify-center gap-2 sm:hidden"
        >
          <Camera size={16} /> Hacer una foto del CV
        </button>
        <input
          ref={camaraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => setFichero(e.target.files?.[0] || null)}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="tel-desde-cv">
              Teléfono <span className="font-normal text-sereno-dark/40">(opcional)</span>
            </label>
            <input
              id="tel-desde-cv"
              className="input h-11"
              type="tel"
              inputMode="tel"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="Si lo tienes a mano"
            />
          </div>
          <div>
            <label className="label" htmlFor="fuente-desde-cv">
              Cómo nos llegó
            </label>
            <select
              id="fuente-desde-cv"
              className="input h-11"
              value={fuente}
              onChange={(e) => setFuente(e.target.value as Fuente)}
            >
              {Object.entries(FUENTE_LABELS).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="text-xs text-sereno-dark/40">
          Se crea la ficha con el CV guardado y después se lee para rellenar los datos. Podrás
          revisarlos antes de que queden en firme.
        </p>

        {error && <div className="card bg-red-50 border-red-200 text-red-700 text-sm py-3">{error}</div>}

        <div className="flex gap-2 justify-end">
          <button onClick={onCerrar} disabled={subiendo} className="btn-ghost h-11 text-sm">
            Cancelar
          </button>
          <button
            onClick={() => void crear()}
            disabled={subiendo || !fichero}
            className="btn-primary h-11 text-sm flex items-center gap-2"
          >
            {subiendo && <Loader2 size={16} className="animate-spin" />}
            {subiendo ? 'Subiendo…' : 'Crear ficha'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
